import { useCallback, useMemo, useRef, useState } from "react";
import useEffectAfterMount from "./useEffectAfterMount.js";
import useOpenState from "./useOpenState/index.js";
import useUniqueId from "./useUniqueId.js";
const noop = ()=>{};
function getElementHeight(el) {
    if (!el || !el.current) return 'auto';
    return el.current.scrollHeight;
}
const callAll = (...fns)=>(...args)=>fns.forEach((fn)=>fn && fn(...args));
const defaultTransitionStyles = {
    transitionDuration: '500ms',
    transitionTimingFunction: 'cubic-bezier(0.250, 0.460, 0.450, 0.940)'
};
function joinTransitionProperties(string) {
    if (string) {
        const styles = [
            'height'
        ];
        styles.push(...string.split(', '));
        return styles.join(', ');
    }
    return 'height';
}
function makeTransitionStyles({ expandStyles = defaultTransitionStyles, collapseStyles = defaultTransitionStyles }) {
    return {
        expandStyles: {
            transitionProperty: joinTransitionProperties(expandStyles.transitionProperty),
            ...expandStyles
        },
        collapseStyles: {
            transitionProperty: joinTransitionProperties(collapseStyles.transitionProperty),
            ...collapseStyles
        }
    };
}
function useCollapse(initialConfig = {}) {
    const uniqueId = useUniqueId();
    const el = useRef(null);
    const [isOpen, setOpen] = useOpenState(initialConfig);
    const collapsedHeight = `${initialConfig.collapsedHeight || 0}px`;
    const collapsedStyles = useMemo(()=>({
            display: '0px' === collapsedHeight ? 'none' : 'block',
            height: collapsedHeight,
            overflow: 'hidden'
        }), [
        collapsedHeight
    ]);
    const { expandStyles, collapseStyles } = useMemo(()=>makeTransitionStyles(initialConfig), [
        initialConfig
    ]);
    const [styles, setStyles] = useState(isOpen ? {} : collapsedStyles);
    const [mountChildren, setMountChildren] = useState(isOpen);
    const toggleOpen = useCallback(()=>setOpen((oldOpen)=>!oldOpen), []);
    useEffectAfterMount(()=>{
        let animationId;
        let secondAnimationId;
        if (isOpen) {
            if (initialConfig.onOpenCallback) initialConfig.onOpenCallback();
            animationId = requestAnimationFrame(()=>{
                setMountChildren(true);
                setStyles((oldStyles)=>({
                        ...oldStyles,
                        ...expandStyles,
                        willChange: 'height',
                        display: 'block',
                        overflow: 'hidden'
                    }));
                secondAnimationId = requestAnimationFrame(()=>{
                    const height = getElementHeight(el);
                    setStyles((oldStyles)=>({
                            ...oldStyles,
                            willChange: 'unset',
                            height
                        }));
                });
            });
        } else {
            if (initialConfig.onCloseCallback) initialConfig.onCloseCallback();
            animationId = requestAnimationFrame(()=>{
                const height = getElementHeight(el);
                setStyles((oldStyles)=>({
                        ...oldStyles,
                        ...collapseStyles,
                        willChange: 'height',
                        height
                    }));
                secondAnimationId = requestAnimationFrame(()=>{
                    setStyles((oldStyles)=>({
                            ...oldStyles,
                            willChange: 'unset',
                            height: collapsedHeight,
                            overflow: 'hidden'
                        }));
                });
            });
        }
        return ()=>{
            if (animationId) cancelAnimationFrame(animationId);
            if (secondAnimationId) cancelAnimationFrame(secondAnimationId);
        };
    }, [
        isOpen
    ]);
    const handleTransitionEnd = (e)=>{
        if (e.target !== el.current) return;
        if (isOpen) {
            const height = getElementHeight(el);
            height === styles.height ? setStyles({}) : setStyles((oldStyles)=>({
                    ...oldStyles,
                    height
                }));
        } else if (styles.height === collapsedHeight) {
            setMountChildren(false);
            setStyles(collapsedStyles);
        }
    };
    return {
        getToggleProps (props = {}) {
            const { disabled = false, onClick = noop, ...rest } = props;
            return {
                role: 'button',
                id: `react-collapsed-toggle-${uniqueId}`,
                'aria-controls': `react-collapsed-panel-${uniqueId}`,
                'aria-expanded': isOpen ? 'true' : 'false',
                tabIndex: 0,
                ...rest,
                onClick: disabled ? noop : callAll(onClick, toggleOpen)
            };
        },
        getCollapseProps (props = {}) {
            const { style = {}, onTransitionEnd = noop, refKey = 'ref', ...rest } = props;
            return {
                id: `react-collapsed-panel-${uniqueId}`,
                'aria-hidden': isOpen ? void 0 : 'true',
                ...rest,
                [refKey]: el,
                onTransitionEnd: callAll(handleTransitionEnd, onTransitionEnd),
                style: {
                    ...defaultTransitionStyles,
                    ...style,
                    transitionProperty: joinTransitionProperties(style.transitionProperty),
                    ...styles
                }
            };
        },
        isOpen,
        toggleOpen,
        mountChildren
    };
}
const src_useCollapse = useCollapse;
export default src_useCollapse;
